import * as React from "react";

import { useAuth } from "@/providers";
import { RobotoLinkHoverUnderline } from "@/shared/components";
import { EmptyState } from "@/shared/components/EmptyState";
import {
  qualifyPrincipal,
  renderPrincipal,
  ResourceView,
} from "@/shared/components/ResourceView";
import { type AgentRecord } from "@/shared/domain/agents";
import { useAgents } from "@/shared/domain/agents/hooks";
import { Banner } from "@/shared/foundation/primitives";
import {
  cellStyles,
  ColumnVisibility,
  DataTable,
  createColumnHelper,
  renderDashOrValue,
  useInMemoryRows,
  useTable,
  type ColDef,
  type ColumnPickerOption,
  deriveLoadingState,
  type ICellRendererParams,
  type InMemorySortKey,
} from "@/shared/foundation/primitives/Table";
import { Goto } from "@/shared/navigation";

import styles from "./AgentsTab.module.css";
import { CreateAgentMenu } from "./CreateAgentMenu";

const columnHelper = createColumnHelper<AgentRecord>();

function colId<T>(col: ColDef<T>): string {
  return col.colId ?? col.field ?? "";
}

/**
 * Module-scope ColDefs so AG Grid sees a stable identity across renders.
 * Navigation lives on the Name cell because ``DataTable`` doesn't expose
 * ``onRowClicked``. Every column sets ``hide: false`` to override the
 * primitive's ``defaultColDef.hide: true`` (consumer opt-in pattern; see
 * ``AgentThreadsTable``).
 */
const columns = [
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 2,
    minWidth: 200,
    sortable: true,
    hide: false,
    cellRenderer: (p: ICellRendererParams<AgentRecord, string | undefined>) => {
      const agent = p.data;
      if (!agent) {
        return null;
      }
      return (
        <RobotoLinkHoverUnderline
          to={Goto.createAgentRoute(agent.agent_id)}
          className={cellStyles.primaryContent}
        >
          {agent.name}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.accessor("description", {
    headerName: "Description",
    flex: 3,
    minWidth: 120,
    hide: false,
    cellRenderer: renderDashOrValue,
  }),
  // A derived value needs the helper's accessor-function form; its field form
  // deliberately forbids a `valueGetter`.
  columnHelper.accessor((agent) => agent.variables.length, {
    colId: "variables",
    headerName: "Variables",
    width: 120,
    hide: false,
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.accessor((agent) => qualifyPrincipal(agent.created_by), {
    colId: "created_by",
    headerName: "Created by",
    width: 180,
    minWidth: 150,
    sortable: true,
    hide: false,
    cellRenderer: renderPrincipal,
  }),
  columnHelper.timestamp("modified", {
    headerName: "Modified",
    width: 200,
    minWidth: 120,
    sortable: true,
    hide: false,
    variant: "relative",
  }),
  columnHelper.accessor((agent) => qualifyPrincipal(agent.modified_by), {
    colId: "modified_by",
    headerName: "Modified by",
    width: 180,
    minWidth: 150,
    sortable: true,
    hide: false,
    cellRenderer: renderPrincipal,
  }),
];

/**
 * Columns shown on a first visit. Everything in `columns` is offered by the
 * picker; anything left out of this list starts hidden. `Modified by` is
 * near-duplicate information next to `Created by` on most rows, so it is
 * available rather than always present.
 *
 * `<DataTable>` overlays these onto each ColDef's own `hide` whenever a seed
 * is supplied, so this list -- not `hide: false` above -- decides what the
 * grid renders at mount.
 */
const DEFAULT_VISIBLE_COLUMN_IDS = columns
  .map(colId)
  .filter((id) => id !== "modified_by");

const PICKER_OPTIONS: ColumnPickerOption[] = columns.map((column) => ({
  id: colId(column),
  label: column.headerName ?? colId(column),
}));

/**
 * How each sortable column compares two agents. The list endpoint takes no
 * sort parameter and `<DataTable>` sorts server-side by contract, so a header
 * without an entry here would move its arrow and reorder nothing -- keep this
 * map and the `sortable: true` flags above in step.
 */
const SORT_KEYS: Record<string, InMemorySortKey<AgentRecord>> = {
  name: (agent) => agent.name,
  created_by: (agent) => agent.created_by,
  modified: (agent) => new Date(agent.modified),
  modified_by: (agent) => agent.modified_by,
};

/**
 * The "Agents" tab body, rendered inside the parent ``AgentsTabs``
 * container.
 *
 * An empty org gets an `EmptyState` card rather than the table's
 * ``noRowsContent`` overlay, because it needs the full pitch and a CTA.
 */
export function AgentsTab() {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;

  const agentsQuery = useAgents({ orgId });
  const table = useTable<AgentRecord>({
    getRowId: (row) => row.agent_id,
    initialSort: { columnId: "modified", direction: "desc" },
    defaultVisibleColumnIds: DEFAULT_VISIBLE_COLUMN_IDS,
  });

  const agents = React.useMemo(
    () => agentsQuery.data?.items ?? [],
    [agentsQuery.data],
  );

  // The whole org's agents arrive in one response, so the header's sort and
  // the paging happen here; the list endpoint has no sort parameter.
  const { visibleRows, totalRowCount, hasNextPage } = useInMemoryRows(
    table,
    agents,
    SORT_KEYS,
  );

  const loading = deriveLoadingState({
    isLoading: agentsQuery.isLoading,
    isFetching: agentsQuery.isFetching,
  });

  if (agentsQuery.isError) {
    return (
      <Banner variant="error" className={styles.errorBanner}>
        Failed to load agents. {agentsQuery.error?.message ?? ""}
      </Banner>
    );
  }

  if (!agentsQuery.isLoading && totalRowCount === 0) {
    return (
      <EmptyState
        title="No agents yet"
        headingLevel={3}
        actions={<CreateAgentMenu variant="secondary" />}
        data-testid="agents-empty"
      >
        <p>
          Agents capture a workflow once — system prompt, goals, label
          vocabulary — and let teammates launch it against new datasets without
          re-declaring the whole request. Create one to get started.
        </p>
      </EmptyState>
    );
  }

  return (
    <>
      <div className={styles.toolbar}>
        <ColumnVisibility
          options={PICKER_OPTIONS}
          visibleColumnIds={table.visibleColumnIds}
          setColumnVisibility={table.setColumnVisibility}
        />
        <CreateAgentMenu />
      </div>
      {agentsQuery.data?.next_token && (
        <Banner className={styles.truncatedNote}>
          Showing the first {agents.length} agents. The rest of this org&apos;s
          agents aren&apos;t listed here.
        </Banner>
      )}
      <div className={styles.tableSlot}>
        <ResourceView.Root>
          <DataTable<AgentRecord>
            table={table}
            // The list endpoint returns no ``total_count`` and nothing here
            // pages past its first response (up to 100 agents), so the total
            // is the rows on hand; the note above says when there are more.
            totalRowCount={totalRowCount}
            hasNextPage={hasNextPage}
            loading={loading}
            columns={columns}
            data={visibleRows}
            noRowsContent="No agents match your filters"
          />
        </ResourceView.Root>
      </div>
    </>
  );
}
